import { ChartOptions, Chart, BarElement } from "chart.js";
import { Bar } from "react-chartjs-2";
import { subdomainData, UserScoreProps } from "./dataTypes";

const verticalLabelPlugin = {
  id: "verticalLabelPlugin",
  afterDatasetsDraw: (chart: Chart) => {
    const { ctx, data, scales } = chart;
    const labels = data.labels as string[] | undefined;
    if (!labels) return;

    const yScale = scales.y;
    if (!yScale) return;

    labels.forEach((label, index) => {
      const barElem = chart.getDatasetMeta(0).data[index] as BarElement | undefined;
      if (!barElem) return;

      const barWidth = (barElem as any).width || 50;

      ctx.save();
      const textWidth = ctx.measureText(label).width;
      ctx.restore();

      const yTop = yScale.getPixelForValue(100);

      const yPos = yTop + 20 + Math.round(textWidth/2);

      let xPos = barElem.x - barWidth / 2 - 10; 

      if (index === 0) {
        xPos += 2; 
      }

      ctx.save();
      ctx.translate(xPos, yPos);
      ctx.rotate(-Math.PI / 2);

      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#000";
      ctx.font = "bold 12px Gully";
      ctx.fillText(label, 0, 0);

      ctx.restore();
    });
  },
}


type DataKey = 'correct' | 'wrong' | 'skipped';

const UserScoreWidget = ({subDomainData,module} : UserScoreProps) => {

    const generateData = (subDomainData: subdomainData[]) => {
        const labels = subDomainData?.map(data => data.subDomain);

        const normalizedData = subDomainData?.map((data) => {
          const total = data.correctquestionscount + data.inCorrectquestioncount + data.ommitedquestioncount;
          
          const correctExact = (data.correctquestionscount / total) * 100;
          const wrongExact = (data.inCorrectquestioncount / total) * 100;
          const skippedExact = (data.ommitedquestioncount / total) * 100;
        
          const correctFloor = Math.floor(correctExact);
          const wrongFloor = Math.floor(wrongExact);
          const skippedFloor = Math.floor(skippedExact);
        
          let sumFloors = correctFloor + wrongFloor + skippedFloor;
          
          let remainder = 100 - sumFloors;
        
          const distribution: Array<{ key: DataKey; fraction: number }> = [
            { key: 'correct', fraction: correctExact - correctFloor },
            { key: 'wrong', fraction: wrongExact - wrongFloor },
            { key: 'skipped', fraction: skippedExact - skippedFloor },
          ];
        
          distribution.sort((a, b) => b.fraction - a.fraction);
        
          const result: Record<DataKey, number> = {
            correct: correctFloor,
            wrong: wrongFloor,
            skipped: skippedFloor,
          };
        
          while (remainder > 0) {
            for (let i = 0; i < distribution.length && remainder > 0; i++) {
              result[distribution[i].key]++;
              remainder--;
            }
          }
        
          return result;
        });
        

        return {
            labels,
            datasets: [
              {
                label: "Correct",
                data: normalizedData?.map((data) => data.correct),
                backgroundColor: "#397367",
                borderRadius: 6,
                categoryPercentage: 0.8,
                barPercentage: 0.7,
              },
              {
                label: "Skipped",
                data: normalizedData?.map((data) => data.skipped),
                backgroundColor: "#FFBA08",
                borderRadius: 6,
                categoryPercentage: 0.8,
                barPercentage: 0.7,
              },
              {
                label: "Wrong",
                data: normalizedData?.map((data) => data.wrong),
                backgroundColor: "#FF0035",
                borderRadius: 6,
                categoryPercentage: 0.8,
                barPercentage: 0.7,
              },
            ],
        };
    }

    const data = generateData(subDomainData);

    const options: ChartOptions<"bar"> = {
      responsive: true,
      maintainAspectRatio:false,
      plugins: {
        datalabels: {
          display: false,
        },
        legend: {
          position: "bottom",
          display: false,
          labels: {
            font: {
              size: 12,
              family: "Gully",
            },
            boxWidth: 14,
            boxHeight: 14,
            textAlign: "center",
          },
        },
        tooltip: {
          enabled: true,
          titleFont: {
            family: "Gully",
            size: 14,
            weight: "bold",
          },
          bodyFont: {
            family: "Gully",
            size: 12,
          },
          footerFont: {
            family: "Gully",
            size: 12,
            style: "italic",
          },
          callbacks: {
            label: (tooltipItem) => {
              const dataset = tooltipItem.dataset;
              const dataIndex = tooltipItem.dataIndex;
              const label = dataset.label || "";
              const value = subDomainData[dataIndex]; // Access the original data
              let questionCount = 0;

              if (label === "Correct") {
                questionCount = value.correctquestionscount;
              } else if (label === "Skipped") {
                questionCount = value.ommitedquestioncount;
              } else if (label === "Wrong") {
                questionCount = value.inCorrectquestioncount;
              }

              return `${label}: ${questionCount} question${questionCount !== 1 ? "s" : ""}`;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: {
            display: false,
          },
          title: {
            display: false,
            text: "Subtopics",
            color: "#000",
            font: {
              size: 14,
              weight: "bold",
              family: "Gully",
            },
          },
          ticks: {
            display: false,
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          max: 100,
          title: {
            display: true,
            text: "Scores in percentage",
            color: "#000",
            font: {
              size: 14,
              weight: "bold",
              family: "Gully",
            },
          },
          ticks:{
            font: {
              size: 12,
              weight: "bold",
              family: "Gully",
            },
          }
        },
      },
    };
    
    return (
      <div className="w-full h-full flex p-3 flex-col justify-between rounded-[18px] bg-satPrepTransWhite backdrop-blur-sevenFive border-[0.5px] border-satPrepGrey shadow-widget-card relative">
        <p className="text-center font-gully text-xl font-medium text-satPrepDarkAccent">
          Performance by subtopic
        </p>
        <p className="text-center font-gully text-base font-normal text-satPrepXDarkAccent">
          {module}
        </p>

        <div className="w-full min-h-[300px] overflow-x-auto widgetScroll">
          <div
            style={{ width: `${subDomainData?.length * 60}px` }}
            className="h-[500px] min-w-full "
          >
            <Bar
              data={data}
              options={options}
              plugins={[verticalLabelPlugin]}
            />
          </div>
        </div>

        <p className="font-bold text-sm font-gully text-center">Subtopics</p>
        <div className="w-full flex justify-center space-x-3">
          <div className="flex">
            <div className="w-4 h-4 bg-satPrepGreen" />
            <p className="font-bold text-xs font-gully text-center ml-2">
              Correct
            </p>
          </div>
          <div className="flex">
            <div className="w-4 h-4 bg-[#FFBA08]" />
            <p className="font-bold text-xs font-gully text-center ml-2">
              Skipped
            </p>
          </div>
          <div className="flex">
            <div className="w-4 h-4 bg-[#FF0035]" />
            <p className="font-bold text-xs font-gully text-center ml-2">
              Wrong
            </p>
          </div>
        </div>
      </div>

    );
}

export default UserScoreWidget

